import { ChangeDetectionStrategy, Component, computed, inject, signal, TemplateRef, ViewChild } from '@angular/core';
import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { BaseDialogComponent, ConfirmDialogComponent, DialogHeaderComponent, DialogSelectionBarComponent } from '@cumlaude/shared-components-dialogs';
import { CategoryField, CategoryFields } from '../../../services/metadata';
import { CheckboxComponent, InstantSearchBoxComponent } from '@cumlaude/shared-components-inputs';
import { includesIgnoreCaseAndDiacritics } from '@cumlaude/shared-utils';
import { Table } from '@cumlaude/metadata';
import { DataExportCategorieComponent } from '../data-export-categorie/data-export-categorie.component';
import { RestService } from '@cumlaude/shared-services';
import { BehaviorSubject, firstValueFrom, lastValueFrom } from 'rxjs';
import {
	DataExportFavorietOpslaanDialogComponent,
	DataExportFavorietOpslaanOptions,
} from '../data-export-favoriet-opslaan-dialog/data-export-favoriet-opslaan-dialog.component';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { UserService } from '../../../services/user.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { DataExportRowComponent } from '../data-export-row/data-export-row.component';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { RDataExportFavoriet } from '@cumlaude/service-contract';
import { DataExport } from '../../../services/export.service';

interface KolommenExportOptions {
	factTable: Table;
	categorieen: CategoryFields[];
	selection: CategoryField[];
}

enum DataExportSelection {
	KOLOMSELECTIE = 'Kolomselectie',
	FAVORIETEN = 'Mijn favorieten',
}

export interface DataExportFavoriet {
	id: string;
	naam: string;
	fieldNames: string;
	favoriet: RDataExportFavoriet;
	attrs: CategoryField[];
}

@Component({
	selector: 'app-data-export-dialog',
	templateUrl: './data-export-dialog.component.html',
	styleUrls: ['./data-export-dialog.component.scss'],
	imports: [
		BaseDialogComponent,
		DialogHeaderComponent,
		ButtonComponent,
		DialogSelectionBarComponent,
		CheckboxComponent,
		InstantSearchBoxComponent,
		DataExportCategorieComponent,
		DataExportRowComponent,
		ConfirmDialogComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataExportDialogComponent {
	private readonly dialogData = inject<KolommenExportOptions>(DIALOG_DATA);
	protected readonly dialogRef = inject<DialogRef<DataExport[]>>(DialogRef);
	private readonly dialog = inject(Dialog);
	protected readonly restService = inject(RestService);
	protected readonly userService = inject(UserService);
	protected readonly toastr = inject(ToastrService);

	@ViewChild('confirmDeleteDialog')
	confirmDeleteDialog!: TemplateRef<DataExportFavoriet>;

	currentTabSelection = signal<DataExportSelection>(DataExportSelection.KOLOMSELECTIE);
	tabSelections: DataExportSelection[] = Object.values(DataExportSelection);

	showSelection = signal(false);
	kolomFilter = signal('');
	favorietFilter = signal('');

	kolommenSelection = signal(this.dialogData.selection);
	favorietenSelection = signal<DataExportFavoriet[]>([]);

	exportSelection = computed(() => {
		const dataExportSelection = this.currentTabSelection();
		switch (dataExportSelection) {
			case DataExportSelection.KOLOMSELECTIE:
				return [
					{
						attrs: this.kolommenSelection(),
					},
				];
			case DataExportSelection.FAVORIETEN:
				return this.favorietenSelection().map((favoriet) => ({
					naam: favoriet.naam,
					attrs: favoriet.attrs,
				}));
		}
	});

	categorieen = signal(this.dialogData.categorieen);

	filteredCategorieen = computed(() => {
		const zoekFilter = this.kolomFilter();
		const showSelection = this.showSelection();
		let categorieen = this.categorieen();

		if (showSelection) {
			categorieen = categorieen.map((cat) => ({
				...cat,
				atts: cat.atts.filter((att) => this.kolommenSelection().some((sel) => sel.att === att.att)),
			}));
		}

		if (zoekFilter.length > 0) {
			const categorieenMatched = categorieen.filter((cat) => includesIgnoreCaseAndDiacritics(cat.name, zoekFilter));
			const categorienWithMatchedAtts = categorieen
				.filter((cat) => !categorieenMatched.some((matched) => matched.name === cat.name))
				.map((cat) => ({ ...cat, atts: cat.atts.filter((att) => kolomContentContains(att, zoekFilter)) }));
			categorieen = [...categorieenMatched, ...categorienWithMatchedAtts].sort((a, b) => a.name.localeCompare(b.name));
		}

		return categorieen.filter((cat) => cat.atts.length > 0);
	});

	private readonly refreshFavorieten$ = new BehaviorSubject<void>(undefined);

	favorieten$ = this.refreshFavorieten$.pipe(
		switchMap(() => this.restService.getDataExportFavorieten({ table: this.dialogData.factTable })),
		map((favorieten) => {
			const fields = this.categorieen().flatMap((cat) => cat.atts);
			return favorieten.map((favoriet) => {
				const attrFields = favoriet.attrs.map((attr) => fields.find((field) => field.att === attr)).filter((field) => field !== undefined);
				return <DataExportFavoriet>{
					id: favoriet.id,
					naam: favoriet.naam,
					attrs: attrFields,
					fieldNames: attrFields
						.map((field) => field.label!)
						.sort((a, b) => a.localeCompare(b))
						.join(', '),
					favoriet: favoriet,
				};
			});
		})
	);

	favorieten = toSignal(this.favorieten$, { initialValue: [] });

	filteredFavorieten = computed(() => {
		const zoekFilter = this.favorietFilter();
		return this.favorieten().filter((favoriet) => includesIgnoreCaseAndDiacritics(favoriet.naam, zoekFilter));
	});

	isAllSelected() {
		return this.kolommenSelection().length === this.categorieen().flatMap((cat) => cat.atts).length;
	}

	isCategorieSelected(categorie: CategoryFields) {
		return this.kolommenSelection().filter((field) => categorie.atts.some((att) => att.att === field.att)).length === categorie.atts.length;
	}

	isColumnSelected(column: CategoryField) {
		return this.kolommenSelection().some((field) => field.att === column.att);
	}

	isFavorietSelected(dataExportFavoriet: DataExportFavoriet) {
		return this.favorietenSelection().some((favoriet) => favoriet.id === dataExportFavoriet.id);
	}

	changeAllOrNoneSelection() {
		if (this.isAllSelected()) {
			this.kolommenSelection.set([]);
		} else {
			this.categorieen().forEach((cat) => {
				this.changeCategorieSelection(cat, true);
			});
		}
	}

	changeCategorieSelection(categorie: CategoryFields, select?: boolean) {
		const selectTarget = select ?? !this.isCategorieSelected(categorie);
		categorie.atts.forEach((att) => {
			this.changeColumnSelection(att, selectTarget);
		});
	}

	changeColumnSelection(column: CategoryField, select?: boolean) {
		const selectTarget = select ?? !this.isColumnSelected(column);
		const currentSelection = this.kolommenSelection();
		if (selectTarget && !this.isColumnSelected(column)) {
			this.kolommenSelection.set([...currentSelection, column]);
		} else if (!selectTarget) {
			this.kolommenSelection.set(currentSelection.filter((field) => field.att !== column.att));
		}
	}

	changeFavorietSelection(dataExportFavoriet: DataExportFavoriet) {
		const currentSelection = this.favorietenSelection();
		if (this.isFavorietSelected(dataExportFavoriet)) {
			this.favorietenSelection.set(currentSelection.filter((favoriet) => favoriet.id !== dataExportFavoriet.id));
		} else {
			this.favorietenSelection.set([...currentSelection, dataExportFavoriet]);
		}
	}

	async saveSelectionAsFavoriet() {
		const dialogRef = this.dialog.open<string>(DataExportFavorietOpslaanDialogComponent, { data: {} });
		const name = await firstValueFrom<string | undefined>(dialogRef.closed);
		if (!name) return;

		const account = await lastValueFrom(this.userService.myAccount$.pipe(take(1)));

		const attrs = this.kolommenSelection().map((field) => field.att);

		const dataExportFavoriet = await firstValueFrom(
			this.restService.postDataExportFavoriet({
				$type: 'favoriet.RDataExportFavoriet',
				table: this.dialogData.factTable,
				naam: name,
				instelling: account.instelling,
				account: account,
				attrs: attrs,
			})
		);
		this.refreshFavorieten$.next();

		const createdFavoriet = await firstValueFrom(
			this.favorieten$.pipe(
				filter((favorieten: DataExportFavoriet[]) => favorieten.some((f) => f.favoriet.id === dataExportFavoriet.id)),
				take(1),
				map((favorieten: DataExportFavoriet[]) => favorieten.find((f) => f.favoriet.id === dataExportFavoriet.id)!)
			)
		);

		this.changeFavorietSelection(createdFavoriet);
		this.currentTabSelection.set(DataExportSelection.FAVORIETEN);

		this.toastr.success(`Favoriet ${name} opgeslagen`);
	}

	async editFavoriet(favoriet: DataExportFavoriet) {
		const dialogRef = this.dialog.open<string, DataExportFavorietOpslaanOptions>(DataExportFavorietOpslaanDialogComponent, {
			data: {
				origineleNaam: favoriet.naam,
			},
		});
		const name = await firstValueFrom<string | undefined>(dialogRef.closed);
		if (!name) return;

		await firstValueFrom(
			this.restService.putDataExportFavoriet({
				...favoriet.favoriet,
				naam: name,
			})
		);
		this.refreshFavorieten$.next();
		this.toastr.success(`Favoriet ${favoriet.naam} hernoemd naar ${name}`);
	}

	async deleteFavoriet(favoriet: DataExportFavoriet) {
		const dialogRef = this.dialog.open(this.confirmDeleteDialog, { data: favoriet });
		if (!(await lastValueFrom(dialogRef.closed))) return;

		try {
			await lastValueFrom(this.restService.deleteDataExportFavoriet(favoriet.favoriet));
			this.refreshFavorieten$.next();
			this.toastr.success(`Dataexportfavoriet ${favoriet.naam} is verwijderd.`);
		} catch (err) {
			this.toastr.error(`Er ging iets fout bij het verwijderen van dataexportfavoriet ${favoriet.naam}.`);
		}
	}

	protected readonly DataExportSelection = DataExportSelection;
}

function kolomContentContains({ label, example, searchKeys }: CategoryField, zoekFilter: string) {
	return (
		includesIgnoreCaseAndDiacritics(label, zoekFilter) ||
		includesIgnoreCaseAndDiacritics(example, zoekFilter) ||
		searchKeys?.some((searchKey) => includesIgnoreCaseAndDiacritics(searchKey, zoekFilter))
	);
}
